const STORAGE_KEY = "cloverleaf-back-url-cache";

interface BackUrlEntry {
  timestamp: number;
  url: string;
}

interface BackUrlCache {
  [hash: string]: BackUrlEntry;
}

/**
 * Simple string hash function (djb2 algorithm)
 * @param str - String to hash
 * @returns Hash as a string
 */
function hashString(str: string): string {
  let hash = 5381;
  for (let i = 0; i < str.length; i++) {
    hash = (hash << 5) + hash + str.charCodeAt(i);
    hash = hash & hash; // Convert to 32-bit integer
  }
  // Convert to positive number and then to hex string
  return Math.abs(hash).toString(16);
}

/**
 * Store a back URL with its hash in localStorage
 * @param hash - The hash of the URL
 * @param url - The actual URL to store
 */
export function storeBackUrl(hash: string, url: string): void {
  if (typeof window === "undefined") {
    return; // SSR - skip localStorage access
  }
  try {
    const cache = getCache();
    const now = Date.now();
    const oneHour = 60 * 60 * 1000; // 1 hour in milliseconds

    // Check if the entry already exists with the same URL
    const existingEntry = cache[hash];
    if (existingEntry && existingEntry.url === url) {
      // If the entry is older than 1 hour, refresh the timestamp
      // to prevent it from being cleaned up prematurely
      if (now - existingEntry.timestamp > oneHour) {
        cache[hash] = {
          url,
          timestamp: now,
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
      }
      // If newer than 1 hour, skip the write to avoid redundant localStorage writes
      return;
    }

    // Entry doesn't exist or has different URL, store it
    cache[hash] = {
      url,
      timestamp: now,
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
  } catch (error) {
    // localStorage may be unavailable (e.g., in SSR or private browsing)
    console.warn("Failed to store back URL:", error);
  }
}

/**
 * Retrieve a back URL by its hash
 * @param hash - The hash to look up
 * @returns The URL if found, null otherwise
 */
export function getBackUrl(hash: string): string | null {
  if (typeof window === "undefined") {
    return null; // SSR - localStorage not available
  }
  try {
    const cache = getCache();
    const entry = cache[hash];
    if (entry) {
      return entry.url;
    }
    return null;
  } catch (error) {
    // localStorage may be unavailable
    console.warn("Failed to get back URL:", error);
    return null;
  }
}

/**
 * Get the cache from localStorage
 * @returns The cache object
 */
function getCache(): BackUrlCache {
  if (typeof window === "undefined") {
    return {}; // SSR - localStorage not available
  }
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored) {
      return JSON.parse(stored) as BackUrlCache;
    }
  } catch (error) {
    // localStorage may be unavailable or corrupted
    console.warn("Failed to read back URL cache:", error);
  }
  return {};
}

/**
 * Remove entries older than the specified number of days
 * @param maxAgeDays - Maximum age in days (default: 7)
 */
export function cleanupOldEntries(maxAgeDays: number = 7): void {
  if (typeof window === "undefined") {
    return; // SSR - skip localStorage access
  }
  try {
    const cache = getCache();
    const now = Date.now();
    const maxAge = maxAgeDays * 24 * 60 * 60 * 1000; // Convert days to milliseconds

    let hasChanges = false;
    for (const hash in cache) {
      const entry = cache[hash];
      if (now - entry.timestamp > maxAge) {
        delete cache[hash];
        hasChanges = true;
      }
    }

    if (hasChanges) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(cache));
    }
  } catch (error) {
    // localStorage may be unavailable
    console.warn("Failed to cleanup back URL cache:", error);
  }
}

/**
 * Hash a URL string
 * @param url - The URL to hash
 * @returns The hash string
 */
export function hashUrl(url: string): string {
  return hashString(url);
}
